import React, { useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { QRCodeCanvas } from 'qrcode.react';
import JsBarcode from 'jsbarcode';
import { useTranslation } from 'react-i18next';

interface LocationBarcodeViewProps {
  open: boolean;
  onClose: () => void;
  code: string;
  name: string;
  barcodeData: string;
}

/**
 * Barkod içeriği "PERON021-RAF004" formatındaysa etikette gösterilecek
 * satırları döner: ["Peron 021", "Raf 004"]. Değilse null (eski düzen kullanılır).
 */
const parsePeronRafLines = (barcodeData: string): string[] | null => {
  const match = /^PERON(\d+)-RAF(\d+)$/i.exec((barcodeData || '').trim());
  if (!match) return null;
  return [`Peron ${match[1]}`, `Raf ${match[2]}`];
};

const LocationBarcodeView: React.FC<LocationBarcodeViewProps> = ({
  open,
  onClose,
  code,
  name,
  barcodeData,
}) => {
  const { t } = useTranslation('warehouse');
  const barcodeRef = useRef<HTMLCanvasElement>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const peronRafLines = parsePeronRafLines(barcodeData);

  useEffect(() => {
    if (open && barcodeRef.current && barcodeData) {
      try {
        JsBarcode(barcodeRef.current, barcodeData, {
          format: 'CODE128',
          width: 1.5,
          height: 50,
          // Peron/Raf etiketinde barkod değeri ayrıca yazdırılmaz; altta
          // "Peron XXX / Raf YYY" satırları basılır (depodaki etiket düzeni).
          displayValue: !peronRafLines,
          fontSize: 12,
          margin: 5,
        });
      } catch {
        // barcode generation may fail for some inputs
      }
    }
  }, [open, barcodeData, peronRafLines]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank', 'width=400,height=400');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head><title>${t('components.locationBarcodeView.printTitle', { code })}</title>
        <style>
          body { margin: 0; padding: 16px; font-family: Arial, sans-serif; text-align: center; }
          .label { border: 1px solid #ccc; padding: 12px; display: inline-block; }
          .name { font-size: 14px; font-weight: bold; margin-bottom: 8px; }
          .code { font-size: 12px; color: #666; margin-bottom: 8px; }
          .line { font-size: 16px; font-weight: bold; margin: 2px 0; }
          canvas, img { max-width: 100%; }
        </style></head>
        <body>
          ${printRef.current.innerHTML}
          <script>window.onload = function() { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('components.locationBarcodeView.title')}</Typography>
          <IconButton edge="end" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box ref={printRef} sx={{ textAlign: 'center', py: 2 }}>
          <div className="label">
            {peronRafLines ? (
              <>
                {/* Depodaki etiket düzeni: üstte barkod, altında Peron/Raf satırları */}
                <Box>
                  <canvas ref={barcodeRef} />
                </Box>
                {peronRafLines.map(line => (
                  <Typography key={line} className="line" variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                    {line}
                  </Typography>
                ))}
              </>
            ) : (
              <>
                <Typography className="name" variant="subtitle1" fontWeight="bold">{name}</Typography>
                <Typography className="code" variant="body2" color="text.secondary" sx={{ mb: 1 }}>{code}</Typography>
                <Box sx={{ mb: 2 }}>
                  <QRCodeCanvas value={barcodeData} size={120} level="M" />
                </Box>
                <Box>
                  <canvas ref={barcodeRef} />
                </Box>
              </>
            )}
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
          {t('components.locationDetailForm.print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationBarcodeView;
